import { useCreateOrganizationDomesticContentMutation } from '@/services/pricing';
import { Box, Button, FormikCheckboxInput, Modal, TextInput, toast } from '@palmetto/palmetto-components';
import { Field, Form, Formik, FormikHelpers, useFormikContext } from 'formik';
import { useCallback, useMemo } from 'react';
import * as yup from 'yup';

import { ProgramType } from 'types';
import { DomesticContentSetting, ItcModifierFormatType, ItcModifierSystemType } from 'types/PricingSettings';

import { DomesticContentModifier } from './types';

const buildHoldbackEntry = (setting: DomesticContentSetting, formValue: string) => ({
  holdback: {
    format: ItcModifierFormatType.perWatt,
    value: setting.modifier?.value === 0 ? (setting.holdback?.value ?? 0) : parseFloat(formValue),
  },
  modifier: { format: ItcModifierFormatType.perWatt, value: setting.modifier.value },
  systemType: setting.systemType,
});

type HoldbackFormValuesType = {
  pvOnly: string;
  storageOnly: string;
  pvAndStorage: string;
  confirmation: boolean;
};

interface EditDomesticContentModalProps {
  programType: ProgramType;
  alias: string;
  isOpen: boolean;
  modifier: DomesticContentModifier | null;
  handleClose: () => void;
}

const buildEditHoldbackSchema = (
  pvOnlyValue?: DomesticContentSetting,
  storageOnlyValue?: DomesticContentSetting,
  pvAndStorageValue?: DomesticContentSetting,
) =>
  yup.object({
    pvOnly: yup
      .number()
      .transform((_value, originalValue) => {
        if (originalValue === '' || originalValue == null) {
          return undefined;
        }
        const parsed = parseFloat(originalValue);
        return isNaN(parsed) ? undefined : parsed;
      })
      .required('PV Only is a required field')
      .min(0, 'Value must be equal or greater than zero')
      .max(pvOnlyValue?.modifier.value ?? 0.5, 'Holdback amount must be less than max allowance'),
    storageOnly: yup
      .number()
      .transform((_value, originalValue) => {
        if (originalValue === '' || originalValue == null) {
          return undefined;
        }
        const parsed = parseFloat(originalValue);
        return isNaN(parsed) ? undefined : parsed;
      })
      .required('Storage Only is a required field')
      .min(0, 'Value must be equal or greater than zero')
      .max(storageOnlyValue?.modifier.value ?? 0.5, 'Holdback amount must be less than max allowance'),
    pvAndStorage: yup
      .number()
      .transform((_value, originalValue) => {
        if (originalValue === '' || originalValue == null) {
          return undefined;
        }
        const parsed = parseFloat(originalValue);
        return isNaN(parsed) ? undefined : parsed;
      })
      .required('PV and Storage is a required field')
      .min(0, 'Value must be equal or greater than zero')
      .max(pvAndStorageValue?.modifier.value ?? 0.5, 'Holdback amount must be less than max allowance'),
    confirmation: yup.boolean().oneOf([true], 'You must confirm before saving').required(),
  });

const confirmationBoxChildGap = { base: 'lg' as const, desktop: 'xl' as const };
const numberInputProps = { step: 0.01 };

const getModifierValue = (value?: number): string => {
  if (value === undefined || value === null) {
    return (0.5).toFixed(2);
  }
  return value.toFixed(2);
};

interface FormContentProps {
  isOpen: boolean;
  modifier: DomesticContentModifier | null;
  pvOnlyValue?: DomesticContentSetting;
  storageOnlyValue?: DomesticContentSetting;
  pvAndStorageValue?: DomesticContentSetting;
  handleClose: () => void;
}

const FormContent = ({
  isOpen,
  modifier,
  pvOnlyValue,
  storageOnlyValue,
  pvAndStorageValue,
  handleClose,
}: FormContentProps) => {
  const { isSubmitting, setFieldValue, touched, handleBlur, resetForm, submitForm, values, errors } =
    useFormikContext<HoldbackFormValuesType>();

  const handleDismiss = useCallback(() => {
    if (!isSubmitting) {
      resetForm();
      handleClose();
    }
  }, [isSubmitting, resetForm, handleClose]);

  const handleNumberFieldChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFieldValue(event.target.name, event.target.value);
    },
    [setFieldValue],
  );

  const handleNumberFieldBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const formatted = parseFloat(event.target.value || '0').toFixed(2);
      setFieldValue(event.target.name, formatted);
      handleBlur(event);
    },
    [setFieldValue, handleBlur],
  );

  const handleConfirmationChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setFieldValue('confirmation', e.target.checked),
    [setFieldValue],
  );

  return (
    <Form>
      <Modal isOpen={isOpen} maxWidth="4xl" ariaLabelledBy="editDomesticContentModal" onDismiss={handleDismiss}>
        <Modal.Header
          id="editDomesticContentModal"
          title={`Holdback Setting for ${modifier?.state || ''}`}
          onDismiss={handleDismiss}
        />
        <Modal.Body background="secondary" childGap="lg">
          <Box gap="lg">
            <Box gap="2xs">
              <TextInput
                type="number"
                label="PV Only"
                name="pvOnly"
                helpText={`Max allowance is $${getModifierValue(pvOnlyValue?.modifier.value)}`}
                id="pvOnly"
                value={values.pvOnly}
                isDisabled={isSubmitting || pvOnlyValue?.modifier.value === 0}
                onChange={handleNumberFieldChange}
                prefix="$"
                suffix="/watt"
                error={touched.pvOnly && (errors.pvOnly as string)}
                onBlur={handleNumberFieldBlur}
                inputProps={numberInputProps}
              />
            </Box>
          </Box>
          <Box gap="lg">
            <Box gap="2xs">
              <TextInput
                type="number"
                label="Storage Only"
                name="storageOnly"
                helpText={`Max allowance is $${getModifierValue(storageOnlyValue?.modifier.value)}`}
                id="storageOnly"
                value={values.storageOnly}
                isDisabled={isSubmitting || storageOnlyValue?.modifier.value === 0}
                onChange={handleNumberFieldChange}
                prefix="$"
                suffix="/watt"
                error={touched.storageOnly && (errors.storageOnly as string)}
                onBlur={handleNumberFieldBlur}
                inputProps={numberInputProps}
              />
            </Box>
          </Box>
          <Box gap="lg">
            <Box gap="2xs">
              <TextInput
                type="number"
                label="PV and Storage"
                name="pvAndStorage"
                helpText={`Max allowance is $${getModifierValue(pvAndStorageValue?.modifier.value)}`}
                id="pvAndStorage"
                value={values.pvAndStorage}
                isDisabled={isSubmitting || pvAndStorageValue?.modifier.value === 0}
                onChange={handleNumberFieldChange}
                prefix="$"
                suffix="/watt"
                error={touched.pvAndStorage && (errors.pvAndStorage as string)}
                onBlur={handleNumberFieldBlur}
                inputProps={numberInputProps}
              />
            </Box>
          </Box>
          <Box childGap={confirmationBoxChildGap} direction="row">
            <Field
              label="I understand and agree that any changes made to holdbacks will apply to accounts that receive a quote following any holdback change. Any account that has already had a contract sent will not be impacted."
              name="confirmation"
              id="confirmation"
              component={FormikCheckboxInput}
              onChange={handleConfirmationChange}
            />
          </Box>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            tone="neutral"
            isLoading={isSubmitting}
            isDisabled={isSubmitting}
            onClick={handleDismiss}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            isLoading={isSubmitting}
            isDisabled={isSubmitting || !values.confirmation}
            type="submit"
            onClick={submitForm}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </Form>
  );
};

export const EditDomesticContentModal = ({
  programType,
  alias,
  isOpen,
  handleClose,
  modifier,
}: EditDomesticContentModalProps) => {
  const [createOrganizationDomesticContentMutation] = useCreateOrganizationDomesticContentMutation();

  const { values = [] } = modifier || {};

  const pvOnlyValue = values.find(
    (value: DomesticContentSetting) => value.systemType === ItcModifierSystemType.pvOnly,
  );
  const storageOnlyValue = values.find(
    (value: DomesticContentSetting) => value.systemType === ItcModifierSystemType.storageOnly,
  );
  const pvAndStorageValue = values.find(
    (value: DomesticContentSetting) => value.systemType === ItcModifierSystemType.pvAndStorage,
  );

  const validationSchema = useMemo(
    () => buildEditHoldbackSchema(pvOnlyValue, storageOnlyValue, pvAndStorageValue),
    [pvOnlyValue, storageOnlyValue, pvAndStorageValue],
  );

  const initialValues = useMemo(
    () => ({
      pvOnly: (pvOnlyValue?.holdback?.value ?? 0).toFixed(2),
      storageOnly: (storageOnlyValue?.holdback?.value ?? 0).toFixed(2),
      pvAndStorage: (pvAndStorageValue?.holdback?.value ?? 0).toFixed(2),
      confirmation: false,
    }),
    [pvOnlyValue, storageOnlyValue, pvAndStorageValue],
  );

  const onSubmit = useCallback(
    async (
      formValues: HoldbackFormValuesType,
      { setSubmitting, resetForm }: FormikHelpers<HoldbackFormValuesType>,
    ) => {
      try {
        const states = modifier?.state ? [modifier.state] : [];
        await createOrganizationDomesticContentMutation({
          alias,
          data: {
            domesticContentHoldbackSettings: [
              ...(pvOnlyValue ? [buildHoldbackEntry(pvOnlyValue, formValues.pvOnly)] : []),
              ...(storageOnlyValue ? [buildHoldbackEntry(storageOnlyValue, formValues.storageOnly)] : []),
              ...(pvAndStorageValue ? [buildHoldbackEntry(pvAndStorageValue, formValues.pvAndStorage)] : []),
            ],
            states,
            programType,
          },
        }).unwrap();
        handleClose();
        toast.success('Holdback amount has been updated');
        resetForm();
      } catch (error: any) {
        console.error(error);
        const message = error?.data?.message ?? 'Could not update holdback';
        toast.error(message);
      } finally {
        setSubmitting(false);
      }
    },
    [
      alias,
      programType,
      modifier,
      pvOnlyValue,
      storageOnlyValue,
      pvAndStorageValue,
      createOrganizationDomesticContentMutation,
      handleClose,
    ],
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
      validateOnBlur
    >
      <FormContent
        isOpen={isOpen}
        modifier={modifier}
        pvOnlyValue={pvOnlyValue}
        storageOnlyValue={storageOnlyValue}
        pvAndStorageValue={pvAndStorageValue}
        handleClose={handleClose}
      />
    </Formik>
  );
};
